import { useState } from "react";
import { supabase } from "@/lib/supabase";

interface MemberInfo {
  id: string;
  full_name: string;
  email: string;
  last_access_link_sent_at: string | null;
  last_access_link_type: string | null;
}

interface Props {
  member: MemberInfo;
  onClose: () => void;
  onSent: (type: "magiclink" | "recovery") => void;
  onToast: (type: "success" | "error", message: string) => void;
}

export default function SendAccessLinkModal({ member, onClose, onSent, onToast }: Props) {
  const [sending, setSending] = useState<"magiclink" | "recovery" | null>(null);

  const sendLink = async (type: "magiclink" | "recovery") => {
    if (sending) return;
    setSending(type);

    try {
      const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
      const pathPrefix = basePath ? `/${basePath}` : "";
      const redirectPath = type === "magiclink" ? "/meu-perfil" : "/recuperar-senha";
      const redirectTo = `${window.location.origin}${pathPrefix}${redirectPath}`;

      const { data, error } = await supabase.functions.invoke("send-access-link", {
        body: {
          email: member.email,
          name: member.full_name,
          type,
          redirectTo,
        },
      });

      if (error) {
        onToast("error", `Falha ao enviar o link: ${error.message || "erro desconhecido"}`);
        return;
      }

      if (!data?.success) {
        onToast("error", `Falha ao enviar o link: ${data?.error || "erro desconhecido"}`);
        return;
      }

      onToast("success", `Link de ${type === "magiclink" ? "acesso" : "senha"} enviado para ${member.full_name}!`);
      onSent(type);
      onClose();
    } catch (err) {
      onToast("error", `Falha ao enviar o link: ${(err as Error)?.message || "erro inesperado"}`);
    } finally {
      setSending(null);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const lastSentLabel = member.last_access_link_sent_at
    ? new Date(member.last_access_link_sent_at).toLocaleString("pt-BR")
    : null;
  const lastSentTypeLabel =
    member.last_access_link_type === "recovery"
      ? "criar / trocar senha"
      : member.last_access_link_type === "magiclink"
        ? "entrar no perfil"
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-6"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-md bg-white rounded-2xl p-6 md:p-8 animate-fade-in-up">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10">
              <i className="ri-magic-line text-primary text-lg" />
            </span>
            <div>
              <h3 className="font-display text-lg text-brown">Enviar link de acesso</h3>
              <p className="font-body text-xs text-brown/50">{member.full_name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer"
            aria-label="Fechar"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <p className="font-body text-sm text-brown/70 leading-relaxed mb-1">
          Para <strong>{member.email}</strong>
        </p>
        <p className="font-body text-sm text-brown/70 leading-relaxed mb-6">
          Escolha o tipo de link que quer enviar:
        </p>

        {lastSentLabel && (
          <div className="mb-6 px-4 py-3 rounded-xl bg-beige/70 border border-nude">
            <p className="font-body text-xs text-brown/60 leading-relaxed">
              <i className="ri-history-line mr-1" />
              Último envio: <strong>{lastSentTypeLabel}</strong> em {lastSentLabel}
            </p>
          </div>
        )}

        <div className="space-y-3">
          {/* Entrar no perfil (magic link) */}
          <button
            onClick={() => sendLink("magiclink")}
            disabled={!!sending}
            className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border border-nude bg-beige/40 hover:bg-beige transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer text-left"
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 shrink-0">
              <i className="ri-login-box-line text-primary text-lg" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-body text-sm text-brown font-medium">Entrar no perfil</span>
              <span className="block font-body text-xs text-brown/50">Link de acesso direto (entra sem senha)</span>
            </span>
            {sending === "magiclink" ? (
              <i className="ri-loader-4-line animate-spin text-primary shrink-0" />
            ) : (
              <i className="ri-arrow-right-s-line text-brown/40 shrink-0" />
            )}
          </button>

          {/* Criar / trocar senha (recovery) */}
          <button
            onClick={() => sendLink("recovery")}
            disabled={!!sending}
            className="w-full flex items-center gap-3 px-4 py-4 rounded-xl border border-nude bg-beige/40 hover:bg-beige transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer text-left"
          >
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 shrink-0">
              <i className="ri-lock-password-line text-accent-700 text-lg" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-body text-sm text-brown font-medium">Criar / trocar senha</span>
              <span className="block font-body text-xs text-brown/50">Abre a tela para definir a senha</span>
            </span>
            {sending === "recovery" ? (
              <i className="ri-loader-4-line animate-spin text-accent-700 shrink-0" />
            ) : (
              <i className="ri-arrow-right-s-line text-brown/40 shrink-0" />
            )}
          </button>
        </div>

        <button
          onClick={onClose}
          disabled={!!sending}
          className="w-full mt-5 px-6 py-3 rounded-full border border-nude text-brown font-body text-sm tracking-wide uppercase hover:bg-beige transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}